// frontend/src/pages/ProfilePage.jsx
// COMPLETE PROFILE OVERHAUL - Unified system with landing dashboard + comprehensive settings
// Replaces both ProfilePage.jsx and ProfileSettings.jsx with modern, engaging design

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { useToasts } from '../components/ToastContext';
import api from '../services/api';

// Icons from react-icons
import { 
  FiUser, FiSettings, FiShield, FiBell, FiSliders, FiAward, 
  FiShoppingBag, FiMoreHorizontal, FiCamera, FiEdit3, FiTrendingUp,
  FiCalendar, FiTarget, FiZap, FiStar, FiMapPin, FiClock,
  FiActivity, FiCheck, FiX, FiDownload, FiLogOut, FiPlus, FiInfo
} from 'react-icons/fi';

// Enhanced modals
import EnhancedAvatarModal from '../components/modals/EnhancedAvatarModal';
import TwoFactorModal from '../components/modals/TwoFactorModal';
import SecurityConfirmModal from '../components/modals/SecurityConfirmModal';
import AchievementDetailModal from '../components/modals/AchievementDetailModal';
import DataExportModal from '../components/modals/DataExportModal';
import PurchaseStateModal from '../components/PurchaseStateModal';
import ThemeCustomizer from '../components/ThemeCustomizer';
import { getDefaultAvatar } from '../utils/avatarUtils';

// Styles
import '../css/Profile.css';

const TABS = [
  { key: 'overview', label: 'Overview', icon: FiUser },
  { key: 'general', label: 'General', icon: FiSettings },
  { key: 'preferences', label: 'Preferences', icon: FiTarget },
  { key: 'security', label: 'Security', icon: FiShield },
  { key: 'notifications', label: 'Notifications', icon: FiBell },
  { key: 'appearance', label: 'Appearance', icon: FiSliders },
  { key: 'achievements', label: 'Achievements', icon: FiAward },
  { key: 'shop', label: 'Shop', icon: FiShoppingBag },
  { key: 'advanced', label: 'Advanced', icon: FiMoreHorizontal }
];

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const { showToast } = useToasts();
  
  // Main state
  const [activeTab, setActiveTab] = useState('overview');
  const [profile, setProfile] = useState(user || {});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data state
  const [stats, setStats] = useState({});
  const [achievements, setAchievements] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [states, setStates] = useState([]);
  const [ownedStates, setOwnedStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([1]); // Always include national pool
  const [auditLog, setAuditLog] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [userPrivileges, setUserPrivileges] = useState({});
  
  // Modal state
  const [modals, setModals] = useState({
    avatar: false,
    twoFactor: false,
    securityConfirm: false,
    achievementDetail: false,
    dataExport: false,
    purchaseState: false,
    themeCustomizer: false
  });
  const [modalData, setModalData] = useState({});
  
  // Form state for each tab
  const [forms, setForms] = useState({
    general: {},
    preferences: {},
    security: {},
    notifications: {}
  });

  const loadProfileData = useCallback(async () => {
  try {
    setLoading(true);
    const [
      profileRes,
      statsRes,
      achievementsRes,
      activityRes,
      statesRes,
      auditRes,
      sessionsRes
    ] = await Promise.all([
      api.get('/users/me'),
      api.get('/users/me/stats'),
      api.get('/achievements/user').catch(err => {
        console.error('Failed to load achievements:', err);
        return { data: [] };
      }),
      api.get('/users/me/activity-feed').catch(err => {
        console.error('Failed to load activity feed:', err);
        return { data: [] };
      }),
      api.get('/states/with-entitlements').catch(err => {
        console.error('Failed to load states:', err);
        return { data: [] };
      }),
      api.get('/users/me/audit-log').catch(err => {
        console.error('Failed to load audit log:', err);
        return { data: [] };
      }),
      api.get('/users/me/sessions').catch(() => ({ data: [] })) // Optional endpoint
    ]);

    setProfile(profileRes.data);
    setStats(statsRes.data?.stats || {});
    setAchievements(Array.isArray(achievementsRes?.data) ? achievementsRes.data : []);
    setRecentActivity(Array.isArray(activityRes?.data) ? activityRes.data : []);

    // Handle new states response structure
    const statesResponse = statesRes?.data || {};
    const ownedStatesData = Array.isArray(statesResponse.states?.owned) ? statesResponse.states.owned : [];
    const availableStatesData = Array.isArray(statesResponse.states?.available) ? statesResponse.states.available : [];
    const userPrivileges = statesResponse.user_privileges || {};
    
    // Combine all states for the states list
    const allStatesData = [
      ...ownedStatesData.map(st => ({ ...st, owned: true })),
      ...availableStatesData.map(st => ({ ...st, owned: false }))
    ];
    
    setStates(allStatesData);
    setOwnedStates(ownedStatesData);
    setAvailableStates(availableStatesData);
    setUserPrivileges(userPrivileges);

    // Set selected states (National Pool is always active, so only track additional states)
    const owned = ownedStatesData.map(st => st.state_id);
    setSelectedStates(owned);

    setAuditLog(Array.isArray(auditRes?.data) ? auditRes.data : []);
    setActiveSessions(Array.isArray(sessionsRes?.data) ? sessionsRes.data : []);

    // Initialize forms with current data
    setForms({
      general: {
        first_name: profileRes.data.first_name || '',
        last_name: profileRes.data.last_name || '',
        email: profileRes.data.email || '',
        bio: profileRes.data.bio || '',
        location: profileRes.data.location || ''
      },
      preferences: {
        default_question_count: profileRes.data.default_question_count || 10,
        difficulty_preference: profileRes.data.difficulty_preference || 'mixed',
        show_explanations: profileRes.data.show_explanations !== false,
        selected_states: owned
      },
      security: {
        current_password: '',
        new_password: '',
        confirm_password: ''
      },
      notifications: {
        email_quiz_reminders: profileRes.data.email_quiz_reminders !== false,
        email_achievements: profileRes.data.email_achievements !== false,
        email_security_alerts: profileRes.data.email_security_alerts !== false,
        email_marketing: profileRes.data.email_marketing !== false
      }
    });

  } catch (err) {
    console.error('Failed to load profile data:', err);
    if (typeof showToast === 'function') {
      showToast(err?.response?.data?.message || 'Failed to load profile data', 'error');
    }
  } finally {
    setLoading(false);
  }
}, [showToast]);

  // Load all data on component mount
  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  const openModal = (modalName, data = {}) => {
    setModals(prev => ({ ...prev, [modalName]: true }));
    setModalData(data);
  };

  const closeModal = (modalName) => {
    setModals(prev => ({ ...prev, [modalName]: false }));
    setModalData({});
  };

  const updateForm = (tab, field, value) => {
    setForms(prev => ({
      ...prev,
      [tab]: { ...prev[tab], [field]: value }
    }));
  };

  
 const saveTabData = async (tab) => {
  console.log('Saving tab data for:', tab, 'with form data:', forms[tab]);
  setSaving(true);
  try {
    const formData = (forms && forms[tab]) || {};

    switch (tab) {
      case 'general': {
        await api.put('/users/me/profile', formData);
        break;
      }

      case 'preferences': {
        console.log('Saving preferences:', formData);
        const response = await api.put('/users/me/preferences', formData);
        console.log('✅ Preferences saved successfully:', response.data);
        break;
      }

      case 'notifications': {
        await api.put('/users/me/notification-settings', formData);
        break;
      }

      case 'security': {
        if (formData && formData.new_password) {
          await api.put('/users/me/password', {
            current_password: formData.current_password || '',
            new_password: formData.new_password,
          });
          // Clear password fields after successful change
          if (typeof setForms === 'function') {
            setForms((prev) => ({
              ...prev,
              security: { current_password: '', new_password: '', confirm_password: '' },
            }));
          }
        }
        break;
      }

      default:
        // Unknown tab: no-op
        break;
    }

    // success actions
    console.log('✅ API call completed successfully! Executing success actions for tab:', tab);
    
    // Show success toast
    if (typeof showToast === 'function') {
      const message = tab === 'preferences' ? 'Preferences saved successfully' : 'Settings saved successfully';
      showToast(message, 'success');
      console.log('Success toast shown');
    }
    
    // Handle UI refresh based on tab type
    if (tab === 'preferences') {
      console.log('🔄 Preferences saved successfully. Keeping current form values to prevent reset.');
      // For preferences, we don't refresh user data to avoid UI reset
      // The form already contains the values that were just saved
    } else {
      // For other tabs, refresh user data as normal
      if (typeof refreshUser === 'function') {
        await refreshUser();
        console.log('User data refreshed');
      }
    }
  } catch (error) {
    console.error('saveTabData error:', error);
    if (typeof showToast === 'function') {
      showToast(error?.response?.data?.message || error.message || 'Failed to save settings', 'error');
    }
  } finally {
    setSaving(false);
  }
};


  const renderOverviewTab = () => (
    <div className="profile-overview">
      {/* Hero Section */}
      <div className="profile-hero">
        <div className="profile-hero-avatar" onClick={() => openModal('avatar')}>
          <div
            className="avatar-image"
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              backgroundImage: `url(${profile.avatar_url || getDefaultAvatar()})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundColor: "#f5f5f5",
              border: "3px solid white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}
            aria-label="Profile picture"
          />
          <div className="avatar-overlay">
            <FiCamera size={24} />
          </div>
        </div>
        
        <div className="profile-hero-info">
          <h1 className="profile-name">
            {profile.first_name} {profile.last_name}
          </h1>
          <p className="profile-username">@{profile.username}</p>
          
          <div className="profile-badges">
            {(profile.user_role === 'Admin' || profile.user_role === 'SuperAdmin') && (
              <span className="badge badge-admin">
                <FiShield size={14} />
                Administrator
              </span>
            )}
            {profile.user_role === 'VIP' && (
              <span className="badge badge-vip">
                <FiAward size={14} />
                VIP Member
              </span>
            )}
            {profile.subscription_status === 'trial' && !['Admin', 'SuperAdmin', 'VIP'].includes(profile.user_role) && (
              <span className="badge badge-trial">
                <FiClock size={14} />
                Trial Member
              </span>
            )}
            {profile.subscription_status === 'active' && !['Admin', 'SuperAdmin', 'VIP'].includes(profile.user_role) && (
              <span className="badge badge-active">
                <FiStar size={14} />
                Premium Member
              </span>
            )}
            <span className="badge badge-states">
              <FiMapPin size={14} />
              National Pool + {ownedStates.length} {ownedStates.length === 1 ? 'State' : 'States'}
            </span>
          </div>
          
          <p className="profile-member-since">
            <FiCalendar size={14} />
            Member since {new Date(profile.created_at).toLocaleDateString('en-US', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </p>
        </div>
        
        <div className="profile-hero-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setActiveTab('general')}
          >
            <FiEdit3 size={16} />
            Edit Profile
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setActiveTab('preferences')}
          >
            <FiSettings size={16} />
            Settings
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <FiTarget />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{stats.quiz_count || 0}</h3>
            <p className="stat-label">Quizzes Taken</p>
            <p className="stat-change positive">+{stats.quizzes_this_week || 0} this week</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FiTrendingUp />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{stats.average_score || 0}%</h3>
            <p className="stat-label">Average Score</p>
            <p className={`stat-change ${stats.score_trend >= 0 ? 'positive' : 'negative'}`}>
              {stats.score_trend >= 0 ? '+' : ''}{stats.score_trend || 0}% from last week
            </p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FiZap />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{stats.current_streak || 0}</h3>
            <p className="stat-label">Day Streak</p>
            <p className="stat-change positive">
              Best: {stats.best_streak || 0} days
            </p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FiAward />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{achievements.filter(a => a.earned).length}</h3>
            <p className="stat-label">Achievements</p>
            <p className="stat-change positive">
              Level {stats.user_level || 1}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="quick-actions-grid">
          <a href="/quiz" className="quick-action-card">
            <FiTarget />
            <span>Take Quiz</span>
          </a>
          <a href="/results" className="quick-action-card">
            <FiActivity />
            <span>View Results</span>
          </a>
          <button 
            className="quick-action-card"
            onClick={() => setActiveTab('shop')}
          >
            <FiShoppingBag />
            <span>Shop States</span>
          </button>
          <button 
            className="quick-action-card"
            onClick={() => setActiveTab('achievements')}
          >
            <FiAward />
            <span>Achievements</span>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {recentActivity.slice(0, 5).map((activity, index) => (
            <div key={index} className="activity-item">
              <div className="activity-icon">
                {activity.type === 'quiz' && <FiTarget />}
                {activity.type === 'achievement' && <FiAward />}
                {activity.type === 'purchase' && <FiShoppingBag />}
                {activity.type === 'profile' && <FiUser />}
              </div>
              <div className="activity-content">
                <p className="activity-description">{activity.description}</p>
                <p className="activity-time">{activity.time_ago}</p>
              </div>
            </div>
          ))}
        </div>
        {recentActivity.length > 5 && (
          <button className="btn btn-link">View All Activity</button>
        )}
      </div>
    </div>
  );

  const renderGeneralTab = () => (
    <div className="profile-tab-content">
      <div className="tab-header">
        <h2>General Settings</h2>
        <p>Manage your personal information and account details</p>
      </div>

      <div className="settings-section">
        <h3>Profile Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              value={forms.general.first_name}
              onChange={(e) => updateForm('general', 'first_name', e.target.value)}
              placeholder="Enter your first name"
            />
          </div>
          
          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              value={forms.general.last_name}
              onChange={(e) => updateForm('general', 'last_name', e.target.value)}
              placeholder="Enter your last name"
            />
          </div>
          
          <div className="form-group form-group-full">
            <label>Email Address</label>
            <div className="email-input-group">
              <input
                type="email"
                value={forms.general.email}
                onChange={(e) => updateForm('general', 'email', e.target.value)}
                placeholder="Enter your email"
                className="email-input"
              />
              <button
                type="button"
                className="btn btn-secondary btn-sm email-verify-btn"
                onClick={async () => {
                  if (!forms.general.email || forms.general.email === profile.email) return;
                  try {
                    setSaving(true);
                    await api.post('/auth/send-email-verification', { new_email: forms.general.email });
                    showToast('Verification email sent! Check your inbox.', 'success');
                  } catch (error) {
                    showToast(error?.response?.data?.message || 'Failed to send verification', 'error');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={!forms.general.email || forms.general.email === profile.email || saving}
              >
                {forms.general.email !== profile.email ? 'Verify' : 'Verified'}
              </button>
            </div>
            <small>Changing your email will require verification</small>
          </div>
          
          <div className="form-group form-group-full">
            <label>Bio</label>
            <textarea
              value={forms.general.bio}
              onChange={(e) => updateForm('general', 'bio', e.target.value)}
              placeholder="Tell us about yourself..."
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={forms.general.location}
              onChange={(e) => updateForm('general', 'location', e.target.value)}
              placeholder="City, State"
            />
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Current Entitlements</h3>
        <div className="entitlements-compact">
          <div className="entitlement-summary">
            <div className="entitlement-core">
              <FiCheck className="entitlement-icon included" />
              <span className="entitlement-text">National Pool (Always Active)</span>
            </div>
            {ownedStates.length > 0 && (
              <div className="entitlement-additional">
                <FiPlus className="entitlement-icon owned" />
                <span className="entitlement-text">
                  {ownedStates.length} Additional State{ownedStates.length !== 1 ? 's' : ''}
                  {userPrivileges.has_admin_access && ' (Admin Access)'}
                </span>
                <div className="entitlement-list">
                  {ownedStates.map(state => (
                    <span key={state.state_id} className="entitlement-tag">
                      {state.state_abbreviation || state.state_name} ({state.question_count || 0} questions)
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          {ownedStates.length === 0 && !userPrivileges.has_admin_access && (
            <button 
              className="btn btn-sm btn-outline"
              onClick={() => setActiveTab('shop')}
            >
              <FiShoppingBag size={16} />
              Browse States
            </button>
          )}
        </div>
      </div>

      <div className="settings-section">
        <h3>Profile Picture</h3>
        <div className="avatar-section">
          <div
            className="avatar-preview"
            style={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              backgroundImage: `url(${profile.avatar_url || getDefaultAvatar()})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundColor: "#f5f5f5",
              border: "2px solid #ddd"
            }}
            aria-label="Profile picture preview"
          />
          <div className="avatar-actions">
            <button 
              className="btn btn-primary"
              onClick={() => openModal('avatar')}
            >
              <FiCamera size={16} />
              Change Picture
            </button>
            <p className="help-text">
              Upload a square image for best results. Max 5MB.
            </p>
          </div>
        </div>
      </div>

      <div className="tab-actions">
        <button 
          className="btn btn-primary"
          onClick={() => saveTabData('general')}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="profile-tab-content">
      <div className="tab-header">
        <h2>Quiz Preferences</h2>
        <p>Customize your testing experience and state selections</p>
      </div>

      <div className="settings-section">
        <h3>Quiz State Selection</h3>
        <div className="state-selection-compact">
          <div className="states-info">
            {userPrivileges.has_admin_access ? (
              <p className="states-notice">
                <FiShield size={16} />
                <strong>Admin Access:</strong> Select which states to include in your quizzes. National Pool is always active.
              </p>
            ) : (
              <p className="states-notice">
                <FiInfo size={16} />
                Select which of your owned states to include in quizzes. National Pool is always active.
              </p>
            )}
          </div>
          
          <div className="states-selection">
            <div className="states-always-active">
              <div className="state-toggle state-always-on">
                <FiCheck className="state-toggle-icon" />
                <span className="state-toggle-name">National Pool</span>
                <span className="state-toggle-status">Always Active</span>
              </div>
            </div>
            
            <div className="states-toggleable">
              {userPrivileges.has_admin_access ? (
                // Admin/VIP users see all states in compact grid
                <div className="states-grid-compact">
                  {states.map(state => (
                    <label key={state.state_id} className="state-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedStates.includes(state.state_id)}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...selectedStates, state.state_id]
                            : selectedStates.filter(id => id !== state.state_id);
                          setSelectedStates(newSelected);
                          updateForm('preferences', 'selected_states', newSelected);
                        }}
                      />
                      <span className="state-checkbox-name">{state.state_abbreviation || state.state_name}</span>
                      <span className="state-checkbox-access">{state.question_count || 0} questions</span>
                    </label>
                  ))}
                </div>
              ) : (
                // Regular users see owned states in compact grid
                <>
                  {ownedStates.length > 0 ? (
                    <div className="states-grid-compact">
                      {ownedStates.map(state => (
                        <label key={state.state_id} className="state-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedStates.includes(state.state_id)}
                            onChange={(e) => {
                              const newSelected = e.target.checked
                                ? [...selectedStates, state.state_id]
                                : selectedStates.filter(id => id !== state.state_id);
                              setSelectedStates(newSelected);
                              updateForm('preferences', 'selected_states', newSelected);
                            }}
                          />
                          <span className="state-checkbox-name">{state.state_abbreviation || state.state_name}</span>
                          <span className="state-checkbox-access">{state.question_count || 0} questions</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="no-additional-states">
                      <p>No additional states owned.</p>
                      <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => setActiveTab('shop')}
                      >
                        <FiShoppingBag size={14} />
                        Browse States
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Quiz Settings</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Default Question Count</label>
            {(profile.user_role === 'Admin' || profile.user_role === 'SuperAdmin') ? (
              <div className="admin-question-count">
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={forms.preferences.default_question_count || ''}
                  onChange={(e) => updateForm('preferences', 'default_question_count', parseInt(e.target.value) || 10)}
                  placeholder="Enter any number (1-1000)"
                  className="admin-number-input"
                />
                <small>Admin: Enter any question count between 1-1000</small>
              </div>
            ) : (
              <select
                value={forms.preferences.default_question_count}
                onChange={(e) => updateForm('preferences', 'default_question_count', parseInt(e.target.value))}
              >
                <option value={5}>5 Questions</option>
                <option value={10}>10 Questions</option>
                <option value={25}>25 Questions</option>
                <option value={50}>50 Questions</option>
                <option value={100}>100 Questions</option>
              </select>
            )}
          </div>
          
          <div className="form-group">
            <label>Difficulty Preference</label>
            <select
              value={forms.preferences.difficulty_preference}
              onChange={(e) => updateForm('preferences', 'difficulty_preference', e.target.value)}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
              <option value="mixed">Mixed (Recommended)</option>
            </select>
          </div>
          
          <div className="form-group form-group-full">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={forms.preferences.show_explanations}
                onChange={(e) => updateForm('preferences', 'show_explanations', e.target.checked)}
              />
              <span>Show detailed explanations after each question</span>
            </label>
          </div>
        </div>
      </div>

      <div className="tab-actions">
        <button 
          className="btn btn-primary"
          onClick={() => saveTabData('preferences')}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="profile-tab-content">
      <div className="tab-header">
        <h2>Security Settings</h2>
        <p>Manage your account security and authentication</p>
      </div>

      <div className="settings-section">
        <h3>Password</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={forms.security.current_password}
              onChange={(e) => updateForm('security', 'current_password', e.target.value)}
              placeholder="Enter current password"
            />
          </div>
          
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={forms.security.new_password}
              onChange={(e) => updateForm('security', 'new_password', e.target.value)}
              placeholder="Enter new password"
            />
          </div>
          
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={forms.security.confirm_password}
              onChange={(e) => updateForm('security', 'confirm_password', e.target.value)}
              placeholder="Confirm new password"
            />
          </div>
        </div>
        
        <button 
          className="btn btn-primary"
          onClick={() => saveTabData('security')}
          disabled={saving || !forms.security.current_password || !forms.security.new_password || 
                   forms.security.new_password !== forms.security.confirm_password}
        >
          {saving ? 'Updating...' : 'Update Password'}
        </button>
      </div>

      <div className="settings-section">
        <h3>Two-Factor Authentication</h3>
        <div className="two-factor-section">
          {profile.two_factor_enabled ? (
            <div className="two-factor-enabled">
              <div className="two-factor-status">
                <FiShield className="icon-success" />
                <div>
                  <p><strong>Two-factor authentication is enabled</strong></p>
                  <p>Your account is protected with 2FA</p>
                </div>
              </div>
              <button 
                className="btn btn-outline btn-danger"
                onClick={() => openModal('twoFactor', { action: 'disable' })}
              >
                Disable 2FA
              </button>
            </div>
          ) : (
            <div className="two-factor-disabled">
              <div className="two-factor-status">
                <FiShield className="icon-warning" />
                <div>
                  <p><strong>Two-factor authentication is disabled</strong></p>
                  <p>Add an extra layer of security to your account</p>
                </div>
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => openModal('twoFactor', { action: 'enable' })}
              >
                Enable 2FA
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="settings-section">
        <h3>Active Sessions</h3>
        <div className="sessions-list">
          {activeSessions.map((session, index) => (
            <div key={index} className="session-item">
              <div className="session-info">
                <p className="session-device">{session.device || 'Unknown Device'}</p>
                <p className="session-details">
                  {session.location} • Last active: {session.last_active}
                </p>
              </div>
              {session.is_current ? (
                <span className="current-session">Current Session</span>
              ) : (
                <button 
                  className="btn btn-sm btn-outline btn-danger"
                  onClick={() => openModal('securityConfirm', { 
                    action: 'revoke_session', 
                    sessionId: session.id 
                  })}
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="settings-section">
        <h3>Recent Security Activity</h3>
        <div className="audit-log">
          {auditLog.slice(0, 10).map((log, index) => (
            <div key={index} className="audit-item">
              <div className="audit-info">
                <p className="audit-action">{log.action_type}</p>
                <p className="audit-details">{log.details}</p>
              </div>
              <span className="audit-time">{log.time_ago}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="profile-tab-content">
      <div className="tab-header">
        <h2>Notification Settings</h2>
        <p>Control how and when you receive notifications</p>
      </div>

      <div className="settings-section">
        <h3>Email Notifications</h3>
        <div className="notifications-grid">
          <div className="notification-item">
            <div className="notification-info">
              <h4>Quiz Reminders</h4>
              <p>Daily study goals and streak warnings</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={forms.notifications.email_quiz_reminders}
                onChange={(e) => updateForm('notifications', 'email_quiz_reminders', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="notification-item">
            <div className="notification-info">
              <h4>Achievement Alerts</h4>
              <p>New badges and milestone unlocks</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={forms.notifications.email_achievements}
                onChange={(e) => updateForm('notifications', 'email_achievements', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="notification-item">
            <div className="notification-info">
              <h4>Security Alerts</h4>
              <p>Login alerts and suspicious activity</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={forms.notifications.email_security_alerts}
                onChange={(e) => updateForm('notifications', 'email_security_alerts', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="notification-item">
            <div className="notification-info">
              <h4>Marketing & Updates</h4>
              <p>Newsletter and feature announcements</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={forms.notifications.email_marketing}
                onChange={(e) => updateForm('notifications', 'email_marketing', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Push Notifications</h3>
        <div className="push-notifications">
          <p className="coming-soon">
            Push notifications will be available when we launch our mobile apps.
          </p>
        </div>
      </div>

      <div className="tab-actions">
        <button 
          className="btn btn-primary"
          onClick={() => saveTabData('notifications')}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Notification Settings'}
        </button>
      </div>
    </div>
  );

  const renderAppearanceTab = () => (
    <div className="profile-tab-content">
      <div className="tab-header">
        <h2>Appearance Settings</h2>
        <p>Customize the look and feel of your experience</p>
      </div>

      <div className="settings-section">
        <h3>Theme Customization</h3>
        <div className="theme-section">
          <p>Use the theme customizer to personalize your interface with custom colors, fonts, and layouts.</p>
          <button 
            className="btn btn-primary"
            onClick={() => openModal('themeCustomizer')}
          >
            <FiSliders size={16} />
            Open Theme Customizer
          </button>
        </div>
      </div>

      <div className="settings-section">
        <h3>Accessibility</h3>
        <div className="accessibility-grid">
          <div className="accessibility-item">
            <h4>High Contrast Mode</h4>
            <label className="toggle-switch">
              <input type="checkbox" />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="accessibility-item">
            <h4>Reduced Motion</h4>
            <label className="toggle-switch">
              <input type="checkbox" />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="accessibility-item">
            <h4>Large Text</h4>
            <label className="toggle-switch">
              <input type="checkbox" />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAchievementsTab = () => (
    <div className="profile-tab-content">
      <div className="tab-header">
        <h2>Achievements</h2>
        <p>Track your progress and unlock new milestones</p>
      </div>

      <div className="achievement-stats">
        <div className="achievement-stat">
          <h3>{achievements.filter(a => a.earned).length}</h3>
          <p>Earned</p>
        </div>
        <div className="achievement-stat">
          <h3>{achievements.length - achievements.filter(a => a.earned).length}</h3>
          <p>Available</p>
        </div>
        <div className="achievement-stat">
          <h3>{stats.user_level || 1}</h3>
          <p>Level</p>
        </div>
      </div>

      <div className="achievements-grid">
        {achievements.map((achievement, index) => (
          <div 
            key={index} 
            className={`achievement-card ${achievement.earned ? 'earned' : 'locked'}`}
            onClick={() => openModal('achievementDetail', achievement)}
          >
            <div className="achievement-icon">
              {achievement.earned ? (
                <FiAward className="icon-earned" />
              ) : (
                <FiAward className="icon-locked" />
              )}
            </div>
            <div className="achievement-info">
              <h4>{achievement.name}</h4>
              <p>{achievement.description}</p>
              {achievement.progress && !achievement.earned && (
                <div className="achievement-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(achievement.current / achievement.required) * 100}%` }}
                    ></div>
                  </div>
                  <span>{achievement.current}/{achievement.required}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderShopTab = () => (
    <div className="profile-tab-content">
      <div className="tab-header">
        <h2>Shop</h2>
        <p>Purchase additional state licenses and manage subscriptions</p>
      </div>

      {/* Only show subscription section for trial users, not admins or VIPs */}
      {(profile.subscription_status === 'trial' && !['Admin', 'SuperAdmin', 'VIP'].includes(profile.user_role)) && (
        <div className="subscription-section">
          <h3>Current Subscription</h3>
          <div className="subscription-card">
            <div className="subscription-info">
              <h4>Trial Membership</h4>
              <p>{profile.trial_days_left || 0} days remaining in trial</p>
            </div>
            <a href="/billing/upgrade" className="btn btn-primary">
              Upgrade Now
            </a>
          </div>
        </div>
      )}

      <div className="available-states-section">
        <h3>Available States</h3>
        <div className="states-shop-grid">
          {availableStates.map(state => (
            <div key={state.state_id} className="state-shop-card">
              <div className="state-shop-info">
                <h4>{state.state_name}</h4>
                <p>{state.question_count || 'N/A'} questions available</p>
                <div className="state-price">
                  ${state.price || '19.99'}
                </div>
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => openModal('purchaseState', state)}
              >
                Purchase
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="owned-states-section">
        <h3>Your States</h3>
        <div className="owned-states-grid">
          <div className="owned-state-card">
            <h4>National Pool</h4>
            <span className="state-status included">Always Included</span>
          </div>
          {ownedStates.map(state => (
            <div key={state.state_id} className="owned-state-card">
              <h4>{state.state_name}</h4>
              <span className="state-status owned">Owned</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAdvancedTab = () => (
    <div className="profile-tab-content">
      <div className="tab-header">
        <h2>Advanced Settings</h2>
        <p>Data management and account controls</p>
      </div>

      <div className="settings-section">
        <h3>Data Export</h3>
        <div className="data-export-section">
          <p>Download your personal data, quiz history, and achievements.</p>
          <button 
            className="btn btn-primary"
            onClick={() => openModal('dataExport')}
          >
            <FiDownload size={16} />
            Export My Data
          </button>
        </div>
      </div>

      <div className="settings-section">
        <h3>Account Management</h3>
        <div className="account-actions">
          <button className="btn btn-outline">
            <FiLogOut size={16} />
            Sign Out All Devices
          </button>
          <button className="btn btn-danger">
            Delete Account
          </button>
        </div>
      </div>

      <div className="settings-section">
        <h3>Beta Features</h3>
        <div className="beta-features">
          <p>Opt in to experimental features and early access.</p>
          <label className="toggle-switch">
            <input type="checkbox" />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'general':
        return renderGeneralTab();
      case 'preferences':
        return renderPreferencesTab();
      case 'security':
        return renderSecurityTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'appearance':
        return renderAppearanceTab();
      case 'achievements':
        return renderAchievementsTab();
      case 'shop':
        return renderShopTab();
      case 'advanced':
        return renderAdvancedTab();
      default:
        return renderOverviewTab();
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Navigation */}
      <nav className="profile-nav">
        <div className="profile-nav-header">
          <h1>Profile</h1>
        </div>
        <div className="profile-nav-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`profile-nav-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <tab.icon size={20} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="profile-main">
        {renderTabContent()}
      </main>

      {/* Modals */}
      {modals.avatar && (
        <EnhancedAvatarModal
          currentAvatar={profile.avatar_url}
          onClose={() => closeModal('avatar')}
          onAvatarChange={async (url) => {
            await api.put('/users/me', { avatar_url: url });
            setProfile(prev => ({ ...prev, avatar_url: url }));
            refreshUser && refreshUser();
            closeModal('avatar');
            showToast('Profile picture updated!', 'success');
          }}
        />
      )}

      {modals.twoFactor && (
        <TwoFactorModal
          action={modalData.action}
          onClose={() => closeModal('twoFactor')}
          onComplete={() => {
            loadProfileData();
            closeModal('twoFactor');
            showToast('2FA settings updated!', 'success');
          }}
        />
      )}

      {modals.securityConfirm && (
        <SecurityConfirmModal
          action={modalData.action}
          data={modalData}
          onClose={() => closeModal('securityConfirm')}
          onConfirm={async () => {
            if (modalData.action === 'revoke_session') {
              await api.delete(`/users/me/sessions/${modalData.sessionId}`);
              loadProfileData();
            }
            closeModal('securityConfirm');
            showToast('Action completed successfully', 'success');
          }}
        />
      )}

      {modals.achievementDetail && (
        <AchievementDetailModal
          achievement={modalData}
          onClose={() => closeModal('achievementDetail')}
        />
      )}

      {modals.dataExport && (
        <DataExportModal
          onClose={() => closeModal('dataExport')}
          onExport={(format) => {
            // Handle data export
            closeModal('dataExport');
            showToast('Data export started', 'success');
          }}
        />
      )}

      {modals.purchaseState && (
        <PurchaseStateModal
          state={modalData}
          onClose={() => closeModal('purchaseState')}
          onPurchased={() => {
            loadProfileData();
            closeModal('purchaseState');
            showToast('State purchased successfully!', 'success');
          }}
        />
      )}

      {modals.themeCustomizer && (
        <div className="modal-backdrop">
          <div className="modal-content theme-modal">
            <div className="modal-header">
              <h3>Theme Customizer</h3>
              <button 
                className="modal-close"
                onClick={() => closeModal('themeCustomizer')}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <ThemeCustomizer />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}